import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { resolveBySerial, padlockStatusLabel, padlockStatusClass } from "../../utils/padlock";
import { fmtDate, fmtCoords } from "../../utils/format";

export default function DashboardPadlockIdAdmin() {
  const { serialNumber } = useParams();
  const navigate = useNavigate();

  const [padlockId, setPadlockId] = useState(null);
  const [data, setData] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // 1) Resolver ID desde serialNumber (centralizado en services/padlocks)
  useEffect(() => {
    let alive = true;
    async function run() {
      if (!serialNumber) {
        setErr(new Error("Falta el serialNumber en la URL."));
        setResolving(false);
        return;
      }
      try {
        setResolving(true);
        setErr(null);
        const found = await resolveBySerial(serialNumber);
        if (alive) {
          if (found?.id) setPadlockId(found.id);
          else setErr(new Error("No se pudo resolver el candado por serialNumber."));
        }
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setResolving(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, [serialNumber]);

  // 2) Traer el dashboard por ID (via endpoints)
  const fetchDashboard = async (id) => {
    setLoading(true);
    setErr(null);
    try {
      const url = `${endpoints.padlock.byId(id)}/dashboard`;
      const res = await api.get(url);
      const payload = res.data?.data ?? res.data;
      setData(payload);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (padlockId) fetchDashboard(padlockId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [padlockId]);

  // ===== Derivados =====
  const padlock = data?.padlock;
  const lastTelemetry = data?.lastTelemetry;
  const lastLocation = data?.lastLocation;
  const metrics = data?.metrics;

  const status = padlock?.status ?? null;
  const statusClass = padlockStatusClass(status);
  const statusLabel = padlockStatusLabel(status);

  const isLoading = resolving || loading;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Dispositivo #{padlock?.id ?? "—"} — {padlock?.name ?? "—"}
        </h1>

        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded border text-blue-600"
            onClick={() => navigate(`/locksadmin/${encodeURIComponent(serialNumber)}/edit`)}
          >
            Editar
          </button>

          <button
            className={`px-3 py-1 rounded border ${
              status === "ACTIVE" ? "text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"
            }`}
            onClick={async () => {
              try {
                if (!padlock?.id) return;
                const newStatus = status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
                await api.patch(`${endpoints.padlock.byId(padlock.id)}/status`, { status: newStatus });
                await fetchDashboard(padlock.id);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                alert("No se pudo actualizar el estado");
              }
            }}
            disabled={!padlock?.id}
          >
            {status === "ACTIVE" ? "Inactivar" : "Activar"}
          </button>

          <button
            className="px-3 py-2 rounded border text-black"
            onClick={() => navigate(`/locksadmin/`)}
          >
            Volver
          </button>
        </div>
      </div>

      {isLoading && <div className="text-gray-500">Cargando…</div>}

      {err && (
        <div className="text-red-600">
          <p className="font-semibold">Error</p>
          <pre className="whitespace-pre-wrap text-sm">{String(err)}</pre>
        </div>
      )}

      {!isLoading && !err && data && (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Estado</div>
              <div className={`text-lg font-semibold ${statusClass}`}>{statusLabel}</div>
              <div className="text-xs text-gray-400 mt-1">
                {padlock?.serialNumber ?? serialNumber}
              </div>
              <div className="text-xs text-gray-400">{padlock?.company?.name ?? "—"}</div>
            </div>

            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Batería</div>
              <div className="text-lg font-semibold">
                {lastTelemetry?.batteryPct != null
                  ? `${lastTelemetry.batteryPct}%`
                  : lastTelemetry?.batteryV != null
                  ? `${lastTelemetry.batteryV} V`
                  : "—"}
              </div>
              <div className="text-xs text-gray-400">
                Última sync: {fmtDate(lastTelemetry?.lastSyncAt)}
              </div>
            </div>

            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Aperturas (total)</div>
              <div className="text-lg font-semibold">{metrics?.openEventsCount ?? 0}</div>
              <div className="text-xs text-gray-400">
                Telemetrías: {metrics?.totalTelemetries ?? 0}
              </div>
            </div>

            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Última localización</div>
              <div className="text-lg font-semibold">{lastLocation?.location || "—"}</div>
              <div className="text-xs text-gray-400">
                {fmtCoords(lastLocation?.latitude, lastLocation?.longitude)}
              </div>
              <div className="text-xs text-gray-400">{fmtDate(lastLocation?.eventAt)}</div>
            </div>
          </div>

          {/* Últimas aperturas */}
          <div className="bg-white p-4 rounded shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Últimas aperturas</h2>
              <span className="text-sm text-gray-500">
                Mostrando {data?.recent?.opens?.length ?? 0} eventos
              </span>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Autorizado</th>
                    <th className="p-2 text-left">Método</th>
                    <th className="p-2 text-left">Autor</th>
                    <th className="p-2 text-left">Coordenadas</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recent?.opens?.length ? (
                    data.recent.opens.map((ev) => (
                      <tr key={ev.id} className="border-b">
                        <td className="p-2">{fmtDate(ev.openedAt)}</td>
                        <td className="p-2">
                          {ev.openAuthorized ? "Sí" : ev.openAuthorized === false ? "No" : "—"}
                        </td>
                        <td className="p-2">{ev.openAuthMethod || "—"}</td>
                        <td className="p-2">{ev.openAuthorName || "—"}</td>
                        <td className="p-2">{fmtCoords(ev.latitude, ev.longitude)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-3 text-gray-500" colSpan={5}>
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sesiones */}
          <div className="bg-white p-4 rounded shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Sesiones Notecard</h2>
              <span className="text-sm text-gray-500">
                Mostrando {data?.recent?.sessions?.length ?? 0} eventos
              </span>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Opened</th>
                    <th className="p-2 text-left">Closed</th>
                    <th className="p-2 text-left">Reason</th>
                    <th className="p-2 text-left">EventId</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recent?.sessions?.length ? (
                    data.recent.sessions.map((se) => (
                      <tr key={se.id} className="border-b">
                        <td className="p-2">{fmtDate(se.eventAt)}</td>
                        <td className="p-2">{se.opened ? "Sí" : se.opened === false ? "No" : "—"}</td>
                        <td className="p-2">{se.closed ? "Sí" : se.closed === false ? "No" : "—"}</td>
                        <td className="p-2">{se.reason || "—"}</td>
                        <td className="p-2">{se.eventId}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-3 text-gray-500" colSpan={5}>
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notehub */}
          <div className="bg-white p-4 rounded shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Últimos eventos Notehub</h2>
              <span className="text-sm text-gray-500">
                Mostrando {data?.recent?.notehub?.length ?? 0} eventos
              </span>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">File</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Ubicación</th>
                    <th className="p-2 text-left">Coords</th>
                    <th className="p-2 text-left">EventId</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recent?.notehub?.length ? (
                    data.recent.notehub.map((ne) => (
                      <tr key={ne.id} className="border-b">
                        <td className="p-2">{fmtDate(ne.eventAt)}</td>
                        <td className="p-2">{ne.file}</td>
                        <td className="p-2">{ne.status || "—"}</td>
                        <td className="p-2">{ne.location || ne.country || "—"}</td>
                        <td className="p-2">{fmtCoords(ne.latitude, ne.longitude)}</td>
                        <td className="p-2">{ne.eventId}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-3 text-gray-500" colSpan={6}>
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}